import { Dispatch, SetStateAction } from 'react';

import { useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import { IEventTag } from '@/types/projects.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import OptionalComponent from '@ui/optional-component/optional-component';
import { ReactSelectInstanceRefObject, TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import Text from '@ui/text/text/text';

type TTagsOptionProps = {
  meta: IEventTag | null;
  displayColorPicker: IEventTag | null;
  option: TReactSelectOption;
  workingTag: IEventTag;
  handleAdd: () => void;
  selectRef: ReactSelectInstanceRefObject;
  setDisplayColorPicker: Dispatch<SetStateAction<IEventTag | null>>;
  setWorkingTag: Dispatch<SetStateAction<IEventTag>>;
  isFocused?: boolean;
};

const TagsOption = ({
  meta,
  displayColorPicker,
  option,
  workingTag,
  handleAdd,
  selectRef,
  setDisplayColorPicker,
  setWorkingTag,
  isFocused,
}: TTagsOptionProps) => {
  const savingProject = useAppSelector(selectIsSavingWorkingProject);

  return (
    <Button
      backgroundColor={
        isFocused ? 'var(--gray-700)' : displayColorPicker?.uuid === option?.value ? 'var(--gray-600)' : 'transparent'
      }
      data-tag-option-focused={Boolean(isFocused).toString()}
      height="36px"
      hoverBackgroundColor="var(--gray-700)"
      className="flex height-100-percent width-100-percent radius-0 text-left tag-select__option"
      disabled={savingProject}
      value={JSON.stringify(option)}
      onClick={() => {
        // if new option, create new tag
        if (option.__isNew__) {
          if (savingProject) return;

          if (workingTag?.name.trim().length) {
            // create new tag
            handleAdd();

            // clear input value
            const select = selectRef?.current;
            if (select) {
              select?.onInputChange('', {
                action: 'set-value',
                prevInputValue: select?.props?.inputValue || '',
              });
            }
          }
          return;
        }

        setDisplayColorPicker((prev) => {
          if (!meta) return null;
          if (prev?.uuid === option.value) return prev;
          return meta;
        });
        if (meta) {
          setWorkingTag(meta);
        }
      }}
    >
      <Container
        width="100%"
        height="100%"
        className="flex-1 cursor-pointer height-100-percent width-100-percent margin-auto-0"
      >
        <Text className="L2" clamp={1}>
          {option.label}
        </Text>
      </Container>
      <OptionalComponent display={!!meta?.color}>
        <div className="square-16 radius-4 margin-auto" style={{ backgroundColor: meta?.color }} />
      </OptionalComponent>
    </Button>
  );
};

export default TagsOption;
